import { useGameEvents } from "@/game/hooks/useGameEvents";
import { GamePlayAnimationType } from "@/game/utils/animationTypes.ts";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import type { AtomicMove, GameState, PlayerColor } from "@nextgammon/shared";
import { API, Constants, Game, Rules } from "@nextgammon/shared";
import { useCallback, useEffect, useRef, useState } from "react";

// Helper function to parse board and dice if they're strings
const parseBoardInGameState = (gameStateSource: GameState): GameState => {
  // Clone to ensure we're not mutating the original source object,
  // especially if it's coming directly from state or props.
  const gameState = structuredClone(gameStateSource);

  // Parse board if it's a string
  if (gameState.board && typeof gameState.board === "string") {
    GameLogger.logInfo(
      "useGamePlayState",
      "-[parseBoardInGameState] Attempting to parse board. Original string:",
      gameState.board
    );
    try {
      const parsedBoardData = JSON.parse(gameState.board);
      // Basic validation: check if it's an array (further checks like length or item structure could be added)
      if (Array.isArray(parsedBoardData)) {
        gameState.board = parsedBoardData;
      } else {
        GameLogger.logError(
          "useGamePlayState",
          "Parsed board data is not an array as expected:",
          parsedBoardData
        );
        // Fallback to a default empty board to prevent crashes
        gameState.board = Array(24)
          .fill(null)
          .map(() => ({ count: 0, player: null }));
      }
    } catch (e) {
      GameLogger.logError(
        "useGamePlayState",
        "Failed to parse board JSON string:",
        e,
        ". Raw board data:",
        gameState.board
      );
      // Fallback to a default empty board
      gameState.board = Array(24)
        .fill(null)
        .map(() => ({ count: 0, player: null }));
    }
  }

  // Parse dice if it's a string
  if (gameState.dice && typeof gameState.dice === "string") {
    GameLogger.logInfo(
      "useGamePlayState",
      "-[parseBoardInGameState] Attempting to parse dice. Original string:",
      gameState.dice
    );
    try {
      const parsedDiceData = JSON.parse(gameState.dice);
      // Ensure it's an array
      if (Array.isArray(parsedDiceData)) {
        gameState.dice = parsedDiceData;
      } else {
        GameLogger.logError(
          "useGamePlayState",
          "Parsed dice data is not an array as expected:",
          parsedDiceData
        );
        gameState.dice = [];
      }
    } catch (e) {
      GameLogger.logError(
        "useGamePlayState",
        "Failed to parse dice JSON string:",
        e,
        ". Raw dice data:",
        gameState.dice
      );
      gameState.dice = [];
    }
  } else if (!gameState.dice) {
    // Ensure dice is at least an empty array
    gameState.dice = [];
  }

  return gameState;
};

// Helper function for deep equality check (basic implementation)
// For production, consider a more robust library like lodash.isEqual or fast-deep-equal.
function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }
  return true;
}

interface GameDetailsType {
  player1Id: string;
  player2Id: string;
  player1Username?: string;
  player2Username?: string;
  status?: string;
  cubeValue?: number;
  cubeOwner?: string | null;
  pendingDoubleFrom?: string | null;
  stakes?: number;
  turnCount?: number;
  doublingAllowed?: boolean;
  currentTurn?: string | null;
  turnStartedAt?: number | null;
  turnDeadline?: number | null;
  timeRemaining?: number | null;
  gameMode: string; // Static but included for simplicity - string for flexibility with API
  timeBank1?: number | null;
  timeBank2?: number | null;
}

interface UseGamePlayStateProps {
  initialGameStateFromLoader: GameState | null;
  initialGameDetailsFromLoader: GameDetailsType | null;
  currentUserId: string | null;
  loadedGameId: string | null;
  onAnimateStep?: (animation: GamePlayAnimationType) => Promise<void>;
  hasJoinedGame?: boolean;
}

interface LocalTurnHistoryEntry {
  gameStateSnapshot: GameState;
  moveMade: AtomicMove;
}

export interface DieState {
  value: number;
  used: boolean;
}

function getRolledDiceForTurn(diceFromState: readonly number[] | undefined | null): DieState[] {
  if (!diceFromState || diceFromState.length === 0) {
    return [];
  }
  let diceValues: number[];
  if (diceFromState.length === 2 && diceFromState[0] === diceFromState[1]) {
    // Doubles
    diceValues = [diceFromState[0], diceFromState[0], diceFromState[0], diceFromState[0]];
  } else {
    diceValues = [...diceFromState];
  }
  return diceValues.map((value) => ({ value, used: false }));
}

function compareDice(
  diceStates1: DieState[],
  diceStates2: DieState[],
  ignoreUsedState?: boolean
): boolean {
  if ((!diceStates1 && diceStates2) || (diceStates1 && !diceStates2)) return false;
  if (!diceStates1 && !diceStates2) return false;
  if (diceStates1.length === 0 && diceStates2.length === 0) return false;
  if (diceStates1.length !== diceStates2.length) return false;
  return diceStates1.every(
    (die, index) =>
      die.value === diceStates2[index].value &&
      (ignoreUsedState || die.used === diceStates2[index].used)
  );
}

interface GamePlayState {
  displayGameState: GameState | null;
  displayGameDetails: GameDetailsType;
  currentDice: DieState[];
  whiteDice: DieState[];
  blackDice: DieState[];
  attemptLocalMove: (move: AtomicMove) => Promise<{ success: boolean; error?: string }>;
  undoLocalMove: () => Promise<void>;
  getCurrentTurnMoves: () => AtomicMove[];
  getCurrentDiceRolled: () => boolean;
  isPlayerTurn: () => boolean;
  actionError: string | null;
  localTurnHistory: LocalTurnHistoryEntry[];
  gameOver: boolean;
  winnerInfo: { id: string; color: PlayerColor; endReason?: string } | null;
}

export function useGamePlayState(props: UseGamePlayStateProps): GamePlayState {
  const {
    initialGameStateFromLoader,
    initialGameDetailsFromLoader,
    currentUserId,
    loadedGameId,
    hasJoinedGame = true
  } = props;

  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const [displayGameState, setDisplayGameState] = useState<GameState | null>(null);
  const lastAnimatedSequence = useRef(-1);

  const [displayGameDetails, setDisplayGameDetails] = useState<GameDetailsType>(
    initialGameDetailsFromLoader || {
      player1Id: "",
      player2Id: "",
      status: undefined,
      gameMode: "standard"
    }
  );
  // Add a ref for displayGameDetails to stabilize callbacks that depend on it
  const displayGameDetailsRef = useRef<GameDetailsType>(displayGameDetails);
  useEffect(() => {
    displayGameDetailsRef.current = displayGameDetails;
  }, [displayGameDetails]);

  const [localTurnHistory, setLocalTurnHistory] = useState<LocalTurnHistoryEntry[]>([]);
  const [currentDice, setCurrentDice] = useState<DieState[]>([]);
  const [whiteDice, setWhiteDice] = useState<DieState[]>([]);
  const [blackDice, setBlackDice] = useState<DieState[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [winnerInfo, setWinnerInfo] = useState<{
    id: string;
    color: PlayerColor;
    endReason?: string;
  } | null>(null);

  // Ref to hold the latest displayGameState for use in stable callbacks
  const displayGameStateRef = useRef<GameState | null>(displayGameState);
  useEffect(() => {
    displayGameStateRef.current = displayGameState;
  }, [displayGameState]);

  const { subscribe, unsubscribe, closeConnection } = useGameEvents(loadedGameId);

  // Track if we've already subscribed to prevent duplicate subscriptions
  const hasSubscribedRef = useRef(false);

  useEffect(() => {
    if (initialGameStateFromLoader && displayGameState === null) {
      const parsedState = parseBoardInGameState(structuredClone(initialGameStateFromLoader));
      setDisplayGameState(parsedState);
      setLocalTurnHistory([]);
      setActionError(null);

      // Check if we have dice
      if (parsedState.dice && parsedState.dice.length > 0) {
        GameLogger.logInfo("useGamePlayState", "Initial state has dice, setting them up");
        const rolledDice = getRolledDiceForTurn(parsedState.dice);

        // Set dice for the current player
        if (parsedState.currentPlayerColor === Constants.PLAYER_WHITE) {
          setWhiteDice(rolledDice);
          setCurrentDice(rolledDice);
          setBlackDice([]);
        } else {
          setBlackDice(rolledDice);
          setCurrentDice(rolledDice);
          setWhiteDice([]);
        }
      } else {
        // Initialize dice arrays as empty
        setWhiteDice([]);
        setBlackDice([]);
        setCurrentDice([]);
      }
    }
  }, [initialGameStateFromLoader, displayGameState, initialGameDetailsFromLoader]);

  useEffect(() => {
    if (initialGameDetailsFromLoader) {
      // Only update from loader if we don't have more recent data
      // This prevents stale SETUP status from overwriting IN_PROGRESS status
      setDisplayGameDetails((prev) => {
        // If we already have IN_PROGRESS status and loader has SETUP, keep current
        if (prev.status === "IN_PROGRESS" && initialGameDetailsFromLoader.status === "SETUP") {
          return prev;
        }
        return initialGameDetailsFromLoader;
      });
    }
  }, [initialGameDetailsFromLoader]);

  const getCurrentPlayerColor = useCallback((): PlayerColor | null => {
    if (!displayGameState) return null;
    return displayGameState.currentPlayerColor;
  }, [displayGameState]);

  const isPlayerTurn = useCallback((): boolean => {
    // Use refs to get stable values that don't change on heartbeat
    const details = displayGameDetailsRef.current;

    if (!displayGameState || !currentUserId || !details || !displayGameState.currentPlayerColor)
      return false;

    if (
      displayGameState.currentPlayerColor === Constants.PLAYER_WHITE &&
      details.player1Id === currentUserId
    ) {
      return true;
    }
    if (
      displayGameState.currentPlayerColor === Constants.PLAYER_BLACK &&
      details.player2Id === currentUserId
    ) {
      return true;
    }
    return false;
  }, [displayGameState, currentUserId]); // No longer depends on displayGameDetails

  const getCurrentDiceRolled = useCallback((): boolean => {
    if (!displayGameState) {
      GameLogger.logInfo("useGamePlayState", "No displayGameState available for dice check.");
      return false;
    }

    // Check if the game state actually has dice rolled
    // This prevents false positives when dice arrays are empty
    if (!displayGameState.dice || displayGameState.dice.length === 0) {
      GameLogger.logDebug("useGamePlayState", "No dice in game state, returning false");
      return false;
    }

    const diceRolled = compareDice(
      displayGameState.currentPlayerColor === Constants.PLAYER_WHITE ? whiteDice : blackDice,
      currentDice
    );

    GameLogger.logDebug("useGamePlayState", "Dice rolled check:", {
      currentPlayerColor: displayGameState.currentPlayerColor,
      whiteDice,
      blackDice,
      currentDice,
      diceRolled,
      gameStateDice: displayGameState.dice
    });

    return diceRolled;
  }, [displayGameState, currentDice, whiteDice, blackDice]);

  // Determine current user's color
  const getCurrentUserColor = useCallback((): PlayerColor | null => {
    // Use refs to get stable values that don't change on heartbeat
    const details = displayGameDetailsRef.current;

    if (!currentUserId || !details) return null;

    if (details.player1Id === currentUserId) {
      return Constants.PLAYER_WHITE;
    } else if (details.player2Id === currentUserId) {
      return Constants.PLAYER_BLACK;
    }
    return null;
  }, [currentUserId]); // No longer depends on displayGameDetails

  // Handle dice roll animation
  const handleDiceRoll = useCallback(async (rolledDice: DieState[]) => {
    setCurrentDice(rolledDice);
    const { onAnimateStep } = propsRef.current;
    if (onAnimateStep) {
      await onAnimateStep(GamePlayAnimationType.Dice);
    }
  }, []);

  // Update dice for both players and persist them across turns
  useEffect(() => {
    GameLogger.logDebug("useGamePlayState", "Dice useEffect triggered", {
      hasDisplayGameState: !!displayGameState,
      dice: displayGameState?.dice
    });

    if (!displayGameState) {
      setCurrentDice([]);
      return;
    }

    const currentUserColor = getCurrentUserColor();
    const currentPlayerColor = displayGameState.currentPlayerColor;

    function processDiceForPlayer(
      rolledDice: DieState[],
      movesMadeThisTurn: AtomicMove[]
    ): DieState[] {
      const updatedDiceState = [...rolledDice];
      for (const move of movesMadeThisTurn) {
        const dieToMarkUsedIndex = updatedDiceState.findIndex(
          (die) => die.value === move.dieValue && !die.used
        );
        if (dieToMarkUsedIndex !== -1) {
          updatedDiceState[dieToMarkUsedIndex] = {
            ...updatedDiceState[dieToMarkUsedIndex],
            used: true
          };
        } else {
          GameLogger.logWarn(
            "useGamePlayState",
            `Could not find unused die ${move.dieValue} to mark as used for move.`,
            { currentDiceState: updatedDiceState, move }
          );
        }
      }
      return updatedDiceState;
    }

    // If dice are present, update the appropriate player's dice
    if (displayGameState.dice && displayGameState.dice.length > 0) {
      const rolledDiceForTurn = getRolledDiceForTurn(displayGameState.dice);

      // Determine which player rolled the dice and update accordingly
      if (currentPlayerColor === Constants.PLAYER_WHITE) {
        // White player rolled dice
        //if (currentUserColor === Constants.PLAYER_WHITE && isPlayerTurn()) {
        if (compareDice(rolledDiceForTurn, whiteDice, true)) {
          // Current user is white and it's their turn - apply local moves
          const updatedDiceState = processDiceForPlayer(
            [...rolledDiceForTurn],
            localTurnHistory.map((entry) => entry.moveMade)
          );

          GameLogger.logInfo(
            "useGamePlayState",
            "Setting white dice (current user is white):",
            updatedDiceState
          );
          setWhiteDice(updatedDiceState);
          setCurrentDice(updatedDiceState);
        } else {
          // Either current user is black, or white rolled but it's not user's turn
          GameLogger.logInfo(
            "useGamePlayState",
            "Setting white dice (opponent white rolled):",
            rolledDiceForTurn
          );
          // Only trigger animation if dice actually changed
          if (!compareDice(rolledDiceForTurn, whiteDice, true)) {
            setWhiteDice(rolledDiceForTurn);
          } else {
            setWhiteDice(rolledDiceForTurn);
          }
        }
      } else if (currentPlayerColor === Constants.PLAYER_BLACK) {
        // Black player rolled dice
        //if (currentUserColor === Constants.PLAYER_BLACK && isPlayerTurn()) {
        if (compareDice(rolledDiceForTurn, blackDice, true)) {
          // Current user is black and it's their turn - apply local moves
          const updatedDiceState = processDiceForPlayer(
            [...rolledDiceForTurn],
            localTurnHistory.map((entry) => entry.moveMade)
          );

          GameLogger.logInfo(
            "useGamePlayState",
            "Setting black dice (current user is black):",
            updatedDiceState
          );
          setBlackDice(updatedDiceState);
          setCurrentDice(updatedDiceState);
        } else {
          // Either current user is white, or black rolled but it's not user's turn
          GameLogger.logInfo(
            "useGamePlayState",
            "Setting black dice (opponent black rolled):",
            rolledDiceForTurn
          );
          // Only trigger animation if dice actually changed
          if (!compareDice(rolledDiceForTurn, blackDice, true)) {
            setBlackDice(rolledDiceForTurn);
          } else {
            setBlackDice(rolledDiceForTurn);
          }
        }
      }
    } else {
      // No dice in the game state - clear currentDice if not player's turn
      if (!isPlayerTurn()) {
        setCurrentDice([]);
      }
    }
  }, [displayGameState, localTurnHistory, isPlayerTurn, getCurrentUserColor]);

  // Crusty-architect's minimal dice animation solution
  useEffect(() => {
    if (
      displayGameState?.sequence &&
      displayGameState.sequence > lastAnimatedSequence.current &&
      displayGameState.dice &&
      displayGameState.dice.length > 0
    ) {
      handleDiceRoll(getRolledDiceForTurn(displayGameState.dice));
      lastAnimatedSequence.current = displayGameState.sequence;
    }
  }, [displayGameState?.sequence, displayGameState?.dice]);

  const handleServerGameUpdate = useCallback(
    async (event: API.Game.GameUpdateEvent & { seq?: number }) => {
      GameLogger.logDebug("useGamePlayState", "Received GAME_UPDATE", {
        seq: event.seq,
        hasCurrentState: !!event.currentState
      });

      const localStateBeforeUpdate = displayGameStateRef.current;
      const serverInitialState = event.initialState;
      const serverCurrentState = event.currentState;

      const { currentUserId, onAnimateStep } = propsRef.current; // Get currentUserId from stable propsRef
      const currentGameDetails = displayGameDetailsRef.current; // Get current details from stable ref
      GameLogger.logInfo(
        "useGamePlayState",
        "Current game details status:",
        currentGameDetails.status
      );
      GameLogger.logInfo("useGamePlayState", "Server current state exists:", !!serverCurrentState);

      setLocalTurnHistory([]); // Always clear local history on server update
      setActionError(null); // Clear any existing action errors

      // Don't clear initial roll display on GAME_UPDATE - let the timer in handleInitialRollComplete handle it
      GameLogger.logInfo(
        "useGamePlayState",
        "Game update processed, current status:",
        currentGameDetails.status
      );

      // Only clear dice state if we're actually switching turns
      // This prevents disrupting dice animations when we get consecutive updates
      // (e.g., one for turn end, one for new dice roll)
      if (
        localStateBeforeUpdate?.currentPlayerColor &&
        serverCurrentState.currentPlayerColor !== localStateBeforeUpdate.currentPlayerColor
      ) {
        GameLogger.logInfo("useGamePlayState", "Turn changed, clearing dice state", {
          previous: localStateBeforeUpdate.currentPlayerColor,
          current: serverCurrentState.currentPlayerColor
        });
        setCurrentDice([]);
      }

      let clientColor: PlayerColor | null = null;
      if (currentUserId && currentGameDetails) {
        // Assuming Player 1 (usually associated with creating the game or being listed first) is White
        // and Player 2 is Black. This might need adjustment if color assignment is dynamic
        // or not strictly tied to player1Id/player2Id positions.
        if (currentGameDetails.player1Id === currentUserId) {
          clientColor = Constants.PLAYER_WHITE;
        } else if (currentGameDetails.player2Id === currentUserId) {
          clientColor = Constants.PLAYER_BLACK;
        }
      }
      const eventPlayerColor = serverInitialState?.currentPlayerColor;

      // If the event describes moves made by the current client's player
      if (clientColor && eventPlayerColor && clientColor === eventPlayerColor) {
        // Directly set to the server's final state for this turn.
        // Use parseBoardInGameState to ensure board is correctly formatted.
        const finalParsedState = parseBoardInGameState(structuredClone(serverCurrentState));

        // Only update and animate if there's an actual difference from what's displayed
        if (!deepEqual(displayGameStateRef.current, finalParsedState)) {
          setDisplayGameState(finalParsedState);
          if (onAnimateStep) {
            await onAnimateStep(GamePlayAnimationType.State); // Allow one animation step to smooth the transition
          }
        }

        // Update dice state after server update for own moves
        if (finalParsedState.dice && finalParsedState.dice.length > 0) {
          const rolledDice = getRolledDiceForTurn(finalParsedState.dice);
          if (clientColor === Constants.PLAYER_WHITE) {
            setWhiteDice(rolledDice);
          } else if (clientColor === Constants.PLAYER_BLACK) {
            GameLogger.logInfo(
              "useGamePlayState",
              "Server update - setting black dice (own move):",
              rolledDice
            );
            setBlackDice(rolledDice);
          }
        }
        return; // Skip the detailed move-by-move animation of own moves
      }

      // --- Original logic for opponent moves or general updates ---
      // (This part is largely the same as before, but now only runs if it's not an echo of our own move)
      if (localStateBeforeUpdate && deepEqual(localStateBeforeUpdate, serverCurrentState)) {
        return;
      }

      if (!onAnimateStep) {
        setDisplayGameState(parseBoardInGameState(structuredClone(event.currentState)));
        return;
      }

      const movesToAnimate = event.currentState.moves.slice(event.initialState.moves.length);

      GameLogger.logInfo("useGamePlayState", `Animate moves: ${movesToAnimate.length}`);

      let currentAnimatedStepState = parseBoardInGameState(structuredClone(event.initialState));

      // Set initial state for animation, then animate if different from current visual
      if (!deepEqual(displayGameStateRef.current, currentAnimatedStepState)) {
        GameLogger.logInfo("useGamePlayState", "Setting initial animated step.");

        // currentAnimatedStepState is already parsed
        setDisplayGameState(structuredClone(currentAnimatedStepState));
        await onAnimateStep(GamePlayAnimationType.State);
      }

      if (movesToAnimate.length === 0 && !deepEqual(event.initialState, event.currentState)) {
        GameLogger.logInfo(
          "useGamePlayState",
          "No moves to animate, but animating for state change."
        );

        // No moves, but state changed (e.g., dice roll update, game over)
        setDisplayGameState(structuredClone(event.currentState));
        await onAnimateStep(GamePlayAnimationType.State);
      } else {
        for (const move of movesToAnimate) {
          const playerMakingTheMove = currentAnimatedStepState.currentPlayerColor;
          if (!playerMakingTheMove) {
            GameLogger.logWarn(
              "useGamePlayState",
              "Cannot animate server move: player color unknown in step state."
            );
            break;
          }

          GameLogger.logDebug("useGamePlayState", "Applying move:", move);

          currentAnimatedStepState = Game.State.applyMove(
            playerMakingTheMove,
            currentAnimatedStepState, // This state should already be parsed from previous step or initial
            move
          );
          // Ensure the result of applyMove is also parsed, as applyMove itself might not guarantee parsed board
          currentAnimatedStepState = parseBoardInGameState(currentAnimatedStepState);

          setDisplayGameState(structuredClone(currentAnimatedStepState));
          await onAnimateStep(GamePlayAnimationType.Checker);

          GameLogger.logDebug("useGamePlayState", "Finished move:", move);
        }
      }

      // Ensure final state matches server\'s, even if loops were skipped or broken
      if (!deepEqual(displayGameStateRef.current, event.currentState)) {
        setDisplayGameState(structuredClone(event.currentState));
        // Potentially one last animation step if the loop didn\'t bring it to currentState
        // or if there were no moves but initial was different from current.
        // However, if movesToAnimate was processed, this should already be the state.
        // This direct set might skip an animation step if the loop was broken.
        // For robust visual consistency, an onAnimateStep() might be needed here too if states differ.
        // Ensure the final state is also parsed.
        setDisplayGameState(parseBoardInGameState(structuredClone(event.currentState)));
      } else if (deepEqual(displayGameStateRef.current, event.currentState)) {
        // If after animation, the displayGameState is already the target server state,
        // no need for a final setDisplayGameState unless it was a direct jump.
        // This branch handles the case where the loop correctly set the final state.
      } else {
        // Fallback if something unexpected happened, ensure final state is set and parsed.
        setDisplayGameState(parseBoardInGameState(structuredClone(event.currentState)));
      }
    },
    [propsRef, displayGameDetailsRef] // displayGameDetailsRef is stable. propsRef is stable.
  );

  const handleServerGameDetailsUpdate = useCallback(
    (event: API.Game.GameDetailsEvent) => {
      GameLogger.logInfo("useGamePlayState", "Received GAME_DETAILS:", event);
      GameLogger.logInfo("useGamePlayState", "Current gameId:", loadedGameId);
      GameLogger.logInfo(
        "useGamePlayState",
        "Event gameId matches:",
        event.gameId === loadedGameId
      );

      if (event.gameId === loadedGameId) {
        GameLogger.logInfo("useGamePlayState", "Updating game details, status:", event.status);
        GameLogger.logInfo(
          "useGamePlayState",
          `Player IDs in event: player1Id=${event.player1Id}, player2Id=${event.player2Id}`
        );
        setDisplayGameDetails((prev) => {
          GameLogger.logInfo(
            "useGamePlayState",
            `Previous player IDs: player1Id=${prev.player1Id}, player2Id=${prev.player2Id}`
          );
          const newDetails = {
            player1Id: event.player1Id && event.player1Id !== "" ? event.player1Id : prev.player1Id,
            player2Id: event.player2Id && event.player2Id !== "" ? event.player2Id : prev.player2Id,
            player1Username: event.player1Username ?? prev.player1Username,
            player2Username: event.player2Username ?? prev.player2Username,
            status: event.status || prev.status, // Use status from event, fallback to previous
            cubeValue: event.cubeValue ?? prev.cubeValue,
            cubeOwner: event.cubeOwner !== undefined ? event.cubeOwner : prev.cubeOwner,
            pendingDoubleFrom:
              event.pendingDoubleFrom !== undefined
                ? event.pendingDoubleFrom
                : prev.pendingDoubleFrom,
            stakes: event.stakes ?? prev.stakes,
            turnCount: event.turnCount ?? prev.turnCount,
            doublingAllowed: event.doublingAllowed ?? prev.doublingAllowed,
            currentTurn: event.currentTurn !== undefined ? event.currentTurn : prev.currentTurn,
            turnStartedAt:
              event.turnStartedAt !== undefined ? event.turnStartedAt : prev.turnStartedAt,
            turnDeadline: event.turnDeadline !== undefined ? event.turnDeadline : prev.turnDeadline,
            timeRemaining:
              event.timeRemaining !== undefined ? event.timeRemaining : prev.timeRemaining,
            timeBank1: event.timeBank1 !== undefined ? event.timeBank1 : prev.timeBank1,
            timeBank2: event.timeBank2 !== undefined ? event.timeBank2 : prev.timeBank2,
            gameMode: prev.gameMode // Preserve gameMode from initial load
          };
          return newDetails;
        });
      }
    },
    [loadedGameId]
  );

  const handleGameEnd = useCallback(
    (event: API.Game.GameEndEvent) => {
      GameLogger.logInfo("useGamePlayState", "Received GAME_END:", event);
      GameLogger.logInfo("useGamePlayState", "GAME_END endReason:", event.endReason);
      GameLogger.logInfo("useGamePlayState", "GAME_END reason:", event.reason);

      if (event.gameId === loadedGameId) {
        GameLogger.logInfo("useGamePlayState", "Setting game over state");
        setGameOver(true);
        setWinnerInfo({
          id: event.winner.id,
          color: event.winner.color as PlayerColor,
          endReason: event.endReason
        });
        // Update the display game state to the final state
        setDisplayGameState(parseBoardInGameState(structuredClone(event.currentState)));
        setLocalTurnHistory([]);
        setActionError(null);

        // Clear timing fields when game ends
        setDisplayGameDetails((prev) => ({
          ...prev,
          status: "FINISHED",
          currentTurn: null,
          timeRemaining: null,
          turnDeadline: null,
          turnStartedAt: null
        }));

        // Close the SSE connection since the game is over
        closeConnection();
      } else {
        GameLogger.logInfo(
          "useGamePlayState",
          `GAME_END event gameId ${event.gameId} doesn't match loaded ${loadedGameId}`
        );
      }
    },
    [loadedGameId, closeConnection]
  );

  const handleDoubleOffered = useCallback(
    (event: API.Game.DoubleOfferedEvent) => {
      GameLogger.logInfo("useGamePlayState", "Received DOUBLE_OFFERED:", event);
      if (event.gameId === loadedGameId) {
        setDisplayGameDetails((prev) => ({
          ...prev,
          pendingDoubleFrom: event.offeredBy
        }));
      }
    },
    [loadedGameId]
  );

  const handleDoubleAccepted = useCallback(
    (event: API.Game.DoubleAcceptedEvent) => {
      GameLogger.logInfo("useGamePlayState", "Received DOUBLE_ACCEPTED:", event);
      if (event.gameId === loadedGameId) {
        setDisplayGameDetails((prev) => ({
          ...prev,
          cubeValue: event.newCubeValue,
          cubeOwner: event.newCubeOwner,
          stakes: event.newStakes,
          pendingDoubleFrom: null
        }));
      }
    },
    [loadedGameId]
  );

  const handleDoubleDeclined = useCallback(
    (event: API.Game.DoubleDeclinedEvent) => {
      GameLogger.logInfo("useGamePlayState", "Received DOUBLE_DECLINED:", event);
      if (event.gameId === loadedGameId) {
        // Game will end, but update the details first
        setDisplayGameDetails((prev) => ({
          ...prev,
          pendingDoubleFrom: null
        }));
        // The GAME_END event will handle the rest
      }
    },
    [loadedGameId]
  );

  // Handler for heartbeat events - update turn timing
  const handleHeartbeat = useCallback(
    (event: API.Game.HeartbeatEvent) => {
      if (event.gameId === loadedGameId) {
        GameLogger.logDebug("useGamePlayState", "Heartbeat received", {
          seq: event.seq
        });
        // Heartbeat no longer contains timing information
        // Timing is handled by GameDetailsEvent updates
      }
    },
    [loadedGameId]
  );

  useEffect(() => {
    if (loadedGameId && hasJoinedGame && subscribe && unsubscribe && !hasSubscribedRef.current) {
      GameLogger.logInfo(
        "useGamePlayState",
        `Subscribing to SSE events for game ${loadedGameId} (hasJoinedGame: ${hasJoinedGame})`
      );
      hasSubscribedRef.current = true;

      subscribe(API.Game.SSEEventType.GAME_UPDATE, handleServerGameUpdate);
      subscribe(API.Game.SSEEventType.GAME_DETAILS, handleServerGameDetailsUpdate);
      subscribe(API.Game.SSEEventType.GAME_END, handleGameEnd);
      subscribe(API.Game.SSEEventType.HEARTBEAT, handleHeartbeat);
      subscribe(API.Game.SSEEventType.DOUBLE_OFFERED, handleDoubleOffered);
      subscribe(API.Game.SSEEventType.DOUBLE_ACCEPTED, handleDoubleAccepted);
      subscribe(API.Game.SSEEventType.DOUBLE_DECLINED, handleDoubleDeclined);

      return () => {
        GameLogger.logInfo(
          "useGamePlayState",
          `Unsubscribing from SSE events for game ${loadedGameId}`
        );
        hasSubscribedRef.current = false;

        unsubscribe(API.Game.SSEEventType.GAME_UPDATE, handleServerGameUpdate);
        unsubscribe(API.Game.SSEEventType.GAME_DETAILS, handleServerGameDetailsUpdate);
        unsubscribe(API.Game.SSEEventType.GAME_END, handleGameEnd);
        unsubscribe(API.Game.SSEEventType.HEARTBEAT, handleHeartbeat);
        unsubscribe(API.Game.SSEEventType.DOUBLE_OFFERED, handleDoubleOffered);
        unsubscribe(API.Game.SSEEventType.DOUBLE_ACCEPTED, handleDoubleAccepted);
        unsubscribe(API.Game.SSEEventType.DOUBLE_DECLINED, handleDoubleDeclined);
      };
    } else if (!loadedGameId || !hasJoinedGame || !subscribe || !unsubscribe) {
      GameLogger.logInfo(
        "useGamePlayState",
        `NOT subscribing: loadedGameId=${loadedGameId}, hasJoinedGame=${hasJoinedGame}, subscribe defined=${!!subscribe}, unsubscribe defined=${!!unsubscribe}, hasSubscribed=${hasSubscribedRef.current}`
      );
    }
  }, [
    loadedGameId,
    hasJoinedGame,
    subscribe,
    unsubscribe,
    handleServerGameUpdate,
    handleServerGameDetailsUpdate,
    handleGameEnd,
    handleHeartbeat,
    handleDoubleOffered,
    handleDoubleAccepted,
    handleDoubleDeclined
  ]);

  const attemptLocalMove = useCallback(
    async (move: AtomicMove): Promise<{ success: boolean; error?: string }> => {
      setActionError(null);
      const playerColor = getCurrentPlayerColor();
      const { onAnimateStep } = propsRef.current;

      if (!displayGameState || !playerColor) {
        const err = "Game state or current player not available for move.";
        setActionError(err);
        return { success: false, error: err };
      }
      if (!isPlayerTurn()) {
        const err = "Not your turn.";
        setActionError(err);
        return { success: false, error: err };
      }
      if (!displayGameState.dice || displayGameState.dice.length === 0) {
        const err = "Dice have not been rolled for this turn.";
        setActionError(err);
        return { success: false, error: err };
      }

      // Check if the specified dieValue is available (i.e., exists and is not used)
      const availableDie = currentDice.find((d) => d.value === move.dieValue && !d.used);

      if (!availableDie) {
        const availableDiceValues = currentDice
          .filter((d) => !d.used)
          .map((d) => d.value)
          .join(", ");
        const err = `Die ${move.dieValue} is not available or already used. Available dice: [${availableDiceValues}]`;
        setActionError(err);
        return { success: false, error: err };
      }

      // Ensure the move is valid with the *current* game state and the *specific die chosen*
      // (Rules.isMoveValid typically checks game rules irrespective of specific die *instance* if dice are just numbers,
      // but here we've confirmed a specific die value is available and unused)
      if (Rules.isMoveValid(displayGameState, move)) {
        const stateBeforeMove = structuredClone(displayGameState);
        let newDisplayState = Game.State.applyMove(playerColor, stateBeforeMove, move);
        newDisplayState = parseBoardInGameState(newDisplayState); // Parse board here

        const newHistoryEntry: LocalTurnHistoryEntry = {
          gameStateSnapshot: stateBeforeMove,
          moveMade: structuredClone(move)
        };
        setLocalTurnHistory((prev) => [...prev, newHistoryEntry]);

        if (onAnimateStep) {
          setDisplayGameState(newDisplayState); // Parsed state
          await onAnimateStep(GamePlayAnimationType.Checker);
        } else {
          setDisplayGameState(newDisplayState); // Parsed state
        }

        return { success: true };
      } else {
        const err = "Invalid move according to game rules.";
        setActionError(err);
        return { success: false, error: err };
      }
    },
    [
      displayGameState,
      getCurrentPlayerColor,
      isPlayerTurn,
      currentDice, // currentDice is DieState[] and its reference changes, so it's a valid dependency
      // localTurnHistory is not directly used for decision making in this callback,
      // but its change triggers the useEffect that recalculates currentDice.
      // If attemptLocalMove itself doesn't need to react to localTurnHistory changes
      // other than what currentDice provides, it might be removable here.
      // For now, keeping it as it was.
      localTurnHistory
    ]
  );

  const undoLocalMove = useCallback(async (): Promise<void> => {
    setActionError(null);
    const { onAnimateStep } = propsRef.current;

    if (localTurnHistory.length > 0) {
      const lastEntry = localTurnHistory[localTurnHistory.length - 1];
      let targetState = structuredClone(lastEntry.gameStateSnapshot);
      targetState = parseBoardInGameState(targetState); // Parse board here

      setLocalTurnHistory((prev) => prev.slice(0, -1));

      if (onAnimateStep) {
        setDisplayGameState(targetState); // Parsed state
        await onAnimateStep(GamePlayAnimationType.Checker);
      } else {
        setDisplayGameState(targetState); // Parsed state
      }
    }
  }, [localTurnHistory]);

  const getCurrentTurnMoves = useCallback((): AtomicMove[] => {
    return localTurnHistory.map((entry) => entry.moveMade);
  }, [localTurnHistory]);

  GameLogger.logDebug("useGamePlayState", "Returning state:", {
    whiteDiceCount: whiteDice.length,
    blackDiceCount: blackDice.length,
    currentDiceCount: currentDice.length,
    whiteDice,
    blackDice
  });

  return {
    displayGameState,
    displayGameDetails,
    currentDice,
    whiteDice,
    blackDice,
    attemptLocalMove,
    undoLocalMove,
    getCurrentTurnMoves,
    getCurrentDiceRolled,
    isPlayerTurn,
    actionError,
    localTurnHistory, // Expose local turn history
    gameOver,
    winnerInfo
  };
}
